import { useState, useEffect } from "react";
import { useAuth } from "../../../hooks/useAuth";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import "./Home.css";
import {
  getDashboardStats,
  getTicketsTrend,
  getResolutionTime,
} from "../../../api/ticketApi.js";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

/**
 * Modern Dashboard Component - 100% API Driven
 * NO DUMMY DATA - Everything from backend
 */
const Home = () => {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const [dashboardData, setDashboardData] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [resolutionData, setResolutionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllDashboardData();
  }, []);

  /**
   * Fetch ALL dashboard data from APIs - NO DUMMY DATA
   */
  const fetchAllDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch comprehensive dashboard statistics
      const statsResponse = await getDashboardStats();
      if (statsResponse.success) {
        setDashboardData(statsResponse.data);
      }

      // Fetch trend data for chart
      const trendResponse = await getTicketsTrend();
      if (trendResponse.success) {
        setTrendData(trendResponse.data);
      }

      // Category data is included in dashboard stats (byCategory field)
      // No separate API call needed

      // Fetch resolution time stats
      const resolutionResponse = await getResolutionTime();
      if (resolutionResponse.success) {
        setResolutionData(resolutionResponse.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        className="home"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "600px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            className="spinner"
            style={{
              width: "60px",
              height: "60px",
              border: "6px solid #f3f3f3",
              borderTop: "6px solid #3bac75",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px",
            }}
          />
          <p style={{ color: "#6c757d", fontSize: "16px" }}>
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="home" style={{ padding: "40px", textAlign: "center" }}>
        <i
          className="las la-exclamation-triangle"
          style={{ fontSize: "48px", color: "#ffa500" }}
        />
        <p style={{ marginTop: "10px", color: "#6c757d" }}>
          Failed to load dashboard
        </p>
        <button
          onClick={fetchAllDashboardData}
          style={{
            marginTop: "20px",
            padding: "10px 20px",
            background: "#3bac75",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const {
    overview,
    byPriority,
    byCategory,
    timeStats,
    recentActivity,
    teamStats,
  } = dashboardData;

  // Chart colors - Soft green palette

  const PRIORITY_COLORS = {
    low: "#8bd4ae", // Soft light green
    medium: "#60c896", // Soft medium green
    high: "#3bac75", // Primary green
    urgent: "#2F8A5D", // Dark green
  };

  // Prepare pie chart data for priorities
  const priorityChartData = [
    { name: "Low", value: byPriority.low || 0, color: PRIORITY_COLORS.low },
    {
      name: "Medium",
      value: byPriority.medium || 0,
      color: PRIORITY_COLORS.medium,
    },
    { name: "High", value: byPriority.high || 0, color: PRIORITY_COLORS.high },
    {
      name: "Urgent",
      value: byPriority.urgent || 0,
      color: PRIORITY_COLORS.urgent,
    },
  ];

  return (
    <div className="modern-dashboard">
      <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .modern-dashboard {
                    padding: 20px;
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 20px;
                }
                .dashboard-card {
                    background: white;
                    border-radius: 12px;
                    padding: 24px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                    transition: transform 0.2s, box-shadow 0.2s;
                }
                .dashboard-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 4px 16px rgba(0,0,0,0.12);
                }
                .stat-grid {
                    grid-column: 1 / -1;
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 16px;
                }
                .stat-card {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }
                .stat-icon {
                    width: 60px;
                    height: 60px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 28px;
                    color: white;
                }
                .stat-content {
                    flex: 1;
                }
                .stat-label {
                    font-size: 13px;
                    color: #6c757d;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 4px;
                }
                .stat-value {
                    font-size: 32px;
                    font-weight: 700;
                    color: #212529;
                }
                .stat-trend {
                    font-size: 12px;
                    color: #28a745;
                    margin-top: 4px;
                }
                .chart-container {
                    grid-column: 1 / -1;
                }
                .recent-activity-grid {
                    grid-column: 1 / -1;
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
                    gap: 20px;
                }
            `}</style>

      {/* Welcome Header */}
      <div className="dashboard-card" style={{ gridColumn: "1 / -1" }}>
        <h1
          style={{
            fontSize: "28px",
            fontWeight: "700",
            marginBottom: "8px",
            color: "#212529",
          }}
        >
          Welcome back, {user?.name}! 👋
        </h1>
        <p style={{ color: "#6c757d", fontSize: "15px" }}>
          {isSuperAdmin
            ? "Here's your complete system overview"
            : "Here's your personal ticket dashboard"}
        </p>
      </div>

      {/* Main Statistics Cards */}
      <div className="stat-grid">
        <div className="stat-card">
          <div
            className="stat-icon"
            style={{
              background: "linear-gradient(135deg, #3bac75 0%, #2d8a5a 100%)",
            }}
          >
            <i className="las la-ticket-alt" />
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Tickets</div>
            <div className="stat-value">{overview.total}</div>
            {timeStats && (
              <div className="stat-trend">
                <i className="las la-arrow-up" /> {timeStats.thisWeek} this week
              </div>
            )}
          </div>
        </div>

        <div className="stat-card">
          <div
            className="stat-icon"
            style={{
              background: "linear-gradient(135deg, #60c896 0%, #3bac75 100%)",
            }}
          >
            <i className="las la-folder-open" />
          </div>
          <div className="stat-content">
            <div className="stat-label">Open</div>
            <div className="stat-value">{overview.open}</div>
          </div>
        </div>

        <div className="stat-card">
          <div
            className="stat-icon"
            style={{
              background: "linear-gradient(135deg, #8bd4ae 0%, #60c896 100%)",
            }}
          >
            <i className="las la-hourglass-half" />
          </div>
          <div className="stat-content">
            <div className="stat-label">In Progress</div>
            <div className="stat-value">{overview.inProgress}</div>
          </div>
        </div>

        <div className="stat-card">
          <div
            className="stat-icon"
            style={{
              background: "linear-gradient(135deg, #2F8A5D 0%, #1e6b47 100%)",
            }}
          >
            <i className="las la-check-circle" />
          </div>
          <div className="stat-content">
            <div className="stat-label">Resolved</div>
            <div className="stat-value">{overview.resolved}</div>
            {timeStats && (
              <div className="stat-trend">
                {timeStats.resolvedThisWeek} resolved this week
              </div>
            )}
          </div>
        </div>

        {isSuperAdmin && (
          <>
            <div className="stat-card">
              <div
                className="stat-icon"
                style={{
                  background:
                    "linear-gradient(135deg, #34d399 0%, #10b981 100%)",
                }}
              >
                <i className="las la-user-check" />
              </div>
              <div className="stat-content">
                <div className="stat-label">Assigned to Me</div>
                <div className="stat-value">{overview.assignedToMe || 0}</div>
              </div>
            </div>

            <div className="stat-card">
              <div
                className="stat-icon"
                style={{
                  background:
                    "linear-gradient(135deg, #a1a1aa 0%, #71717a 100%)",
                }}
              >
                <i className="las la-exclamation-triangle" />
              </div>
              <div className="stat-content">
                <div className="stat-label">Unassigned</div>
                <div className="stat-value">{overview.unassigned || 0}</div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Time-Based Statistics */}
      {timeStats && (
        <div className="dashboard-card" style={{ gridColumn: "1 / -1" }}>
          <h3
            style={{
              marginBottom: "20px",
              fontSize: "18px",
              fontWeight: "600",
            }}
          >
            Time-Based Overview
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "16px",
            }}
          >
            <div
              style={{
                textAlign: "center",
                padding: "16px",
                background: "#f8f9fa",
                borderRadius: "8px",
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#3bac75",
                }}
              >
                {timeStats.today}
              </div>
              <div
                style={{ fontSize: "13px", color: "#6c757d", marginTop: "4px" }}
              >
                Created Today
              </div>
            </div>
            <div
              style={{
                textAlign: "center",
                padding: "16px",
                background: "#f8f9fa",
                borderRadius: "8px",
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#60c896",
                }}
              >
                {timeStats.thisWeek}
              </div>
              <div
                style={{ fontSize: "13px", color: "#6c757d", marginTop: "4px" }}
              >
                Created This Week
              </div>
            </div>
            <div
              style={{
                textAlign: "center",
                padding: "16px",
                background: "#f8f9fa",
                borderRadius: "8px",
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#8bd4ae",
                }}
              >
                {timeStats.thisMonth}
              </div>
              <div
                style={{ fontSize: "13px", color: "#6c757d", marginTop: "4px" }}
              >
                Created This Month
              </div>
            </div>
            {resolutionData && (
              <div
                style={{
                  textAlign: "center",
                  padding: "16px",
                  background: "#f8f9fa",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    color: "#2F8A5D",
                  }}
                >
                  {resolutionData.overall.averageHours}h
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#6c757d",
                    marginTop: "4px",
                  }}
                >
                  Avg Resolution Time
                </div>
              </div>
            )}
            {timeStats.overdue > 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "16px",
                  background: "#fef3e2",
                  borderRadius: "8px",
                  border: "1px solid #f59e0b",
                }}
              >
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    color: "#d97706",
                  }}
                >
                  {timeStats.overdue}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#92400e",
                    marginTop: "4px",
                  }}
                >
                  Overdue Tickets
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Monthly Trend Chart */}
      {trendData && trendData.length > 0 && (
        <div className="dashboard-card chart-container">
          <h3
            style={{
              marginBottom: "20px",
              fontSize: "18px",
              fontWeight: "600",
            }}
          >
            Monthly Ticket Trend
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis
                dataKey="month"
                stroke="#6c757d"
                style={{ fontSize: "12px" }}
              />
              <YAxis stroke="#6c757d" style={{ fontSize: "12px" }} />
              <Tooltip
                contentStyle={{
                  background: "white",
                  border: "1px solid #dee2e6",
                  borderRadius: "6px",
                  padding: "12px",
                }}
              />
              <Legend wrapperStyle={{ fontSize: "13px", paddingTop: "16px" }} />
              <Line
                type="monotone"
                dataKey="open"
                stroke="#60c896"
                strokeWidth={2}
                name="Open"
              />
              <Line
                type="monotone"
                dataKey="inProgress"
                stroke="#8bd4ae"
                strokeWidth={2}
                name="In Progress"
              />
              <Line
                type="monotone"
                dataKey="resolved"
                stroke="#3bac75"
                strokeWidth={2}
                name="Resolved"
              />
              <Line
                type="monotone"
                dataKey="closed"
                stroke="#a1a1aa"
                strokeWidth={2}
                name="Closed"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Priority Distribution and Category Charts - Side by Side */}
      {(byPriority &&
        byPriority.low +
          byPriority.medium +
          byPriority.high +
          byPriority.urgent >
          0) ||
      (byCategory && byCategory.length > 0) ? (
        <div
          style={{
            gridColumn: "1 / -1",
            display: "grid",
            gridTemplateColumns: "2fr 3fr",
            gap: "20px",
          }}
        >
          {/* Priority Distribution Pie Chart */}
          {byPriority &&
            byPriority.low +
              byPriority.medium +
              byPriority.high +
              byPriority.urgent >
              0 && (
              <div
                style={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "24px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "20px",
                    fontSize: "18px",
                    fontWeight: "600",
                  }}
                >
                  Priority Distribution
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={priorityChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} (${(percent * 100).toFixed(0)}%)`
                      }
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {priorityChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

          {/* Category Breakdown */}
          {byCategory && byCategory.length > 0 && (
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <h3
                style={{
                  marginBottom: "20px",
                  fontSize: "18px",
                  fontWeight: "600",
                }}
              >
                <i className="las la-tags" /> Tickets by Category
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={byCategory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                  <XAxis
                    dataKey="category"
                    stroke="#6c757d"
                    style={{ fontSize: "12px" }}
                  />
                  <YAxis stroke="#6c757d" style={{ fontSize: "12px" }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3bac75" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      ) : null}

      {/* Recent Activity */}
      {recentActivity && (
        <div className="recent-activity-grid">
          {/* Recent Tickets */}
          <div className="dashboard-card">
            <h3
              style={{
                marginBottom: "20px",
                fontSize: "18px",
                fontWeight: "600",
              }}
            >
              Recent Tickets
            </h3>
            {recentActivity.tickets && recentActivity.tickets.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {recentActivity.tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    onClick={() => navigate(`/view-ticket/${ticket.id}`)}
                    style={{
                      padding: "12px",
                      background: "#f8f9fa",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "background 0.2s",
                      border: "1px solid #dee2e6",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#e9ecef")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#f8f9fa")
                    }
                  >
                    <div
                      style={{
                        fontWeight: "600",
                        color: "#3bac75",
                        fontSize: "13px",
                      }}
                    >
                      {ticket.ticketNumber}
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#212529",
                        marginTop: "4px",
                      }}
                    >
                      {ticket.subject}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6c757d",
                        marginTop: "4px",
                        display: "flex",
                        gap: "12px",
                      }}
                    >
                      <span>
                        <i className="las la-user" /> {ticket.creatorName}
                      </span>
                      <span>
                        <i className="las la-clock" />{" "}
                        {new Date(
                          ticket.created_at || ticket.createdAt
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#6c757d",
                }}
              >
                <i
                  className="las la-inbox"
                  style={{
                    fontSize: "36px",
                    marginBottom: "10px",
                    display: "block",
                  }}
                />
                <p>No recent tickets</p>
              </div>
            )}
          </div>

          {/* Recent Comments */}
          {isSuperAdmin && (
            <div className="dashboard-card">
              <h3
                style={{
                  marginBottom: "20px",
                  fontSize: "18px",
                  fontWeight: "600",
                }}
              >
                Recent Comments
              </h3>
              {recentActivity.comments && recentActivity.comments.length > 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {recentActivity.comments.map((comment) => (
                    <div
                      key={comment.id}
                      onClick={() =>
                        comment.ticket &&
                        navigate(`/view-ticket/${comment.ticketId}`)
                      }
                      style={{
                        padding: "12px",
                        background: "#f8f9fa",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "background 0.2s",
                        border: "1px solid #dee2e6",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#e9ecef")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "#f8f9fa")
                      }
                    >
                      {comment.ticket && (
                        <div
                          style={{
                            fontWeight: "600",
                            color: "#3bac75",
                            fontSize: "13px",
                          }}
                        >
                          {comment.ticket.ticketNumber} -{" "}
                          {comment.ticket.subject}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#212529",
                          marginTop: "4px",
                          fontStyle: "italic",
                        }}
                      >
                        "{comment.comment.substring(0, 100)}
                        {comment.comment.length > 100 ? "..." : ""}"
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6c757d",
                          marginTop: "4px",
                        }}
                      >
                        <i className="las la-user" /> {comment.authorName} •{" "}
                        {new Date(
                          comment.created_at || comment.createdAt
                        ).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#6c757d",
                  }}
                >
                  <i
                    className="las la-comment-slash"
                    style={{
                      fontSize: "36px",
                      marginBottom: "10px",
                      display: "block",
                    }}
                  />
                  <p>No recent comments</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Team Performance (SUPERADMIN only) */}
      {isSuperAdmin && teamStats && teamStats.topCreators && (
        <div className="dashboard-card" style={{ gridColumn: "1 / -1" }}>
          <h3
            style={{
              marginBottom: "20px",
              fontSize: "18px",
              fontWeight: "600",
            }}
          >
            Top Ticket Creators
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "12px",
            }}
          >
            {teamStats.topCreators.slice(0, 10).map((creator, index) => (
              <div
                key={creator.creatorId}
                style={{
                  padding: "12px 16px",
                  background:
                    index === 0
                      ? "linear-gradient(135deg, #3bac75 0%, #2F8A5D 100%)"
                      : "#f8f9fa",
                  borderRadius: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  border:
                    index === 0 ? "2px solid #3bac75" : "1px solid #dee2e6",
                }}
              >
                <div>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: "14px",
                      color: index === 0 ? "white" : "#212529",
                    }}
                  >
                    {index === 0 && (
                      <i
                        className="las la-crown"
                        style={{
                          color: index === 0 ? "white" : "#3bac75",
                          marginRight: "6px",
                        }}
                      />
                    )}
                    {creator.creatorName}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: index === 0 ? "rgba(255,255,255,0.9)" : "#6c757d",
                    }}
                  >
                    {creator.creatorEmail}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: index === 0 ? "white" : "#3bac75",
                  }}
                >
                  {creator.ticketCount}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
